import { useMemo, useState } from "react";
import { StyleSheet, Switch, View } from "react-native";

import { AppText, Button, ButtonText, Stack, TextField } from "~/components/ui";
import { useCareActions, usePlantCareSchedules } from "~/state/care";
import {
  spacing,
  useTheme,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import type { CareTaskKind } from "~/api/types";

const taskOptions: { kind: CareTaskKind; label: string }[] = [
  { kind: "water", label: "Water" },
  { kind: "feed", label: "Feed" },
  { kind: "repot", label: "Repot" },
];

function formatDueAt(value: string) {
  try {
    return new Date(value).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return value;
  }
}

function CareTaskRow({
  plantId,
  kind,
  label,
}: {
  plantId: string;
  kind: CareTaskKind;
  label: string;
}) {
  const { colors } = useTheme();
  const stylesheet = useThemedStyles(makeStyles);
  const schedules = usePlantCareSchedules(plantId);
  const actions = useCareActions();
  const schedule = useMemo(
    () => schedules.find((candidate) => candidate.task_kind === kind) ?? null,
    [kind, schedules],
  );
  const [intervalDraft, setIntervalDraft] = useState("7");
  const [draftTouched, setDraftTouched] = useState(false);
  const shownInterval = draftTouched
    ? intervalDraft
    : String(schedule?.interval_days ?? 7);

  const intervalDays = Math.max(1, Math.trunc(Number(shownInterval)) || 7);
  const enabled = schedule?.enabled ?? false;

  function persistInterval(nextValue: string) {
    setDraftTouched(true);
    setIntervalDraft(nextValue);
    const parsed = Math.max(1, Math.trunc(Number(nextValue)) || 0);
    if (!parsed) return;
    if (schedule || enabled) {
      actions.upsertSchedule(plantId, {
        taskKind: kind,
        intervalDays: parsed,
        enabled: schedule?.enabled ?? true,
      });
    }
  }

  function toggleEnabled(next: boolean) {
    if (schedule) {
      actions.setEnabled(schedule.id, next);
      if (next && schedule.interval_days !== intervalDays) {
        actions.upsertSchedule(plantId, {
          taskKind: kind,
          intervalDays,
          enabled: next,
        });
      }
      return;
    }
    if (next) {
      actions.upsertSchedule(plantId, {
        taskKind: kind,
        intervalDays,
        enabled: true,
      });
    }
  }

  return (
    <View style={stylesheet.row}>
      <View style={stylesheet.rowHeader}>
        <AppText size="sm" weight="semibold">
          {label}
        </AppText>
        <Switch
          accessibilityLabel={`Enable ${label.toLowerCase()} reminders`}
          value={enabled}
          onValueChange={toggleEnabled}
          trackColor={{ false: colors.borderStrong, true: colors.primary }}
        />
      </View>
      <View style={stylesheet.field}>
        <AppText size="xs" weight="semibold">
          Interval (days)
        </AppText>
        <TextField
          accessibilityLabel={`${label} interval days`}
          value={shownInterval}
          keyboardType="number-pad"
          onChangeText={persistInterval}
          onBlur={() => {
            if (schedule || enabled) {
              actions.upsertSchedule(plantId, {
                taskKind: kind,
                intervalDays,
                enabled: schedule?.enabled ?? true,
              });
              setIntervalDraft(String(intervalDays));
              setDraftTouched(true);
            }
          }}
        />
      </View>
      {schedule ? (
        <Stack gap="xs">
          <AppText size="xs">
            Next due: {formatDueAt(schedule.next_due_at)}
          </AppText>
          <Button
            variant="outline"
            compact
            align="start"
            accessibilityLabel={`Mark ${label.toLowerCase()} done`}
            onPress={() => actions.completeTask(schedule.id)}
          >
            <ButtonText>Mark done</ButtonText>
          </Button>
        </Stack>
      ) : null}
    </View>
  );
}

export function PlantCareReminders({ plantId }: { plantId: string }) {
  return (
    <Stack gap="md">
      <AppText size="xs">
        Set how often to water, feed, or repot. Push reminders arrive when a
        task is due (on native apps with notifications enabled).
      </AppText>
      {taskOptions.map((task) => (
        <CareTaskRow
          key={task.kind}
          plantId={plantId}
          kind={task.kind}
          label={task.label}
        />
      ))}
    </Stack>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    row: {
      gap: spacing.s,
      paddingVertical: spacing.xs,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: colors.border,
    },
    rowHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.s,
    },
    field: { gap: spacing.xs, maxWidth: 160 },
  });
